import React, { lazy, Suspense } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

import { useAuth } from './contexts/user-context';
import SEO from './components/SEO';
import Footer from './components/Footer';
import PWAInstallPrompt from './components/PWAInstallPrompt';


// Initialize offline system
import './offline/index.js';

// Lazy loaded pages
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const TelegramLogin = lazy(() => import('./pages/TelegramLogin'));
const MembershipExpired = lazy(() => import('./pages/MembershipExpired'));
const AppRoutes = lazy(() => import('./routes/AppRoutes'));
const Features = lazy(() => import('./pages/Features'));
const About = lazy(() => import('./pages/About'));
const Pricing = lazy(() => import('./pages/Pricing'));
const Landing = lazy(() => import('./pages/Landing'));
const GymDetails = lazy(() => import('./pages/GymDetails'));
// components
import Navbar from './components/Navbar';
import Loader from './components/Loader';
import NetworkReloadHandler from './components/NetworkReloadHandler';
import WeakConnectionWarning from './components/WeakConnectionWarning';
import OfflineReloadBlocker from './components/OfflineReloadBlocker';

/* ---------- ROLE MAP n ---------- */

const ROLE_DASHBOARD = {
  gym: '/dashboard',
  trainer: '/dashboard',
  athlete: '/dashboard-athlete',
};

/* ---------- GUARDS ---------- */

const PublicOnlyRoute = ({ children }) => {
  const { isLoggedIn, loading } = useAuth();

  if (loading) return <Loader />;

  if (isLoggedIn) {
    return <Navigate to={ROLE_DASHBOARD['gym']} replace />;
  }

  return children;
};

const RoleRoute = ({ allowedRoles, children }) => {
  const { user, isLoggedIn, loading } = useAuth();

  if (loading) return <Loader />;

  if (!isLoggedIn) {
    return <Navigate to="/login" replace />;
  }

  if (!allowedRoles.includes(user.role)) {
    return <Navigate to={ROLE_DASHBOARD[user.role] || '/'} replace />;
  }

  return children;
};
const RootRedirect = () => {
  const { isLoggedIn, loading, user } = useAuth();

  if (loading) return <Loader />;

  if (isLoggedIn) {
    const dashboardPath = ROLE_DASHBOARD[user.role] || '/dashboard';
    return <Navigate to={dashboardPath} replace />;
  }

  return <Navigate to="/landing" replace />;
};

const ConditionalFooter = () => {
  const location = useLocation();
  const { isLoggedIn } = useAuth();
  const isDashboardRoute =
    location.pathname.startsWith('/dashboard') ||
    location.pathname.startsWith('/dashboard-athlete');
  const isLandingRoute = location.pathname === '/landing';

  // Don't show footer if user is logged in, except on landing page
  if ((isLoggedIn && !isLandingRoute) || isDashboardRoute) return null;
  return <Footer />;
};
/* ---------- APP ----------this is  */

function App() {
  const { membershipExpired } = useAuth();

  return (
    <>
      <BrowserRouter>
        <NetworkReloadHandler />
      <WeakConnectionWarning />
      <OfflineReloadBlocker />
      <SEO />
      <PWAInstallPrompt />
      <Toaster
        position="top-center"
        reverseOrder={false}
        toastOptions={{
          style: { zIndex: 9999 },
          duration: 2000,
          error: {
            duration: 2000,
          }
        }}
      />
      <Suspense fallback={<Loader />}>
        <Routes>
          {/* Membership Expired Page - Always accessible when expired */}
          {membershipExpired && (
            <Route path="*" element={<MembershipExpired />} />
          )}

          {/* Public */}
          <Route path="/" element={<RootRedirect />} />

          <Route
            path="/landing"
            element={
              <>
                <Navbar />
                <Landing />
                <ConditionalFooter />
              </>
            }
          />
          

          <Route
            path="/login"
            element={
              <>
                <Login />
                <ConditionalFooter />
              </>
            }
          />

          <Route
            path="/register"
            element={
              <>
                <Register />
                <ConditionalFooter />
              </>
            }
          />

          {/* Public Pages */}
          <Route
            path="/features"
            element={
              <>
                <Features />
                <ConditionalFooter />
              </>
            }
          />
          <Route
            path="/about"
            element={
              <>
                <About />
                <ConditionalFooter />
              </>
            }
          />
          <Route
            path="/pricing"
            element={
              <>
                <Pricing />
                <ConditionalFooter />
              </>
            }
          />
          <Route
            path="/gym/:id"
            element={
              <>
                <GymDetails />
                <ConditionalFooter />
              </>
            }
          />

          {/* Dashboard (Gym + Trainer) */}
          <Route
            path="/dashboard/*"
            element={
              <RoleRoute allowedRoles={['gym', 'trainer', 'athlete']}>
                <Suspense fallback={<Loader />}>
                  <AppRoutes />
                </Suspense>
              </RoleRoute>
            }
          />
          {/* Fallback */}
          {/* <Route path="*" element={<Navigate to="/" replace />} /> */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Suspense>
      </BrowserRouter>
    </>
  );
}

export default App;
